import { useCallback, useEffect, useState, type ReactNode } from "react";
import { ActionButton, AuthShell, Form, ResultNotice, SubmitButton, TextField, useAction } from "../components";
import { useAuthUi } from "../context";
import { AuthSessionRequiredError } from "../errors";
import { formatDate } from "../i18n";
import type { AccountPasskey, AccountSnapshot, ActionResult, AuthApi, AuthPageProps } from "../types";

function AccountSection({ title, intro, children }: { title: string; intro?: string; children: ReactNode }) {
  return <section className="account-section"><h2>{title}</h2>{intro ? <p className="section-intro">{intro}</p> : null}{children}</section>;
}

type AccountMutation = (action: () => Promise<ActionResult> | ActionResult) => Promise<void>;

interface AccountMutationHandlers {
  genericError: string;
  successMessage: string;
  navigate?: ((url: string) => void) | undefined;
  reload: () => Promise<void>;
  setNotice: (result: ActionResult | null) => void;
}

export async function runAccountMutation(
  action: () => Promise<ActionResult> | ActionResult,
  handlers: AccountMutationHandlers,
): Promise<void> {
  try {
    const result = await action();
    handlers.setNotice(
      result.message
        ? result
        : result.ok
          ? { ...result, message: handlers.successMessage }
          : { ok: false, message: handlers.genericError },
    );
    if (result.ok && result.redirectTo && handlers.navigate) {
      handlers.navigate(result.redirectTo);
      return;
    }
    if (result.ok) await handlers.reload();
  } catch {
    handlers.setNotice({ ok: false, message: handlers.genericError });
  }
}

interface AccountLoadHandlers {
  genericError: string;
  navigate?: ((url: string) => void) | undefined;
  setAccount: (account: AccountSnapshot | null) => void;
  setError: (message: string | null) => void;
}

export async function loadAccountSnapshot(
  api: Pick<AuthApi, "getAccount">,
  handlers: AccountLoadHandlers,
): Promise<void> {
  try {
    handlers.setAccount(await api.getAccount());
    handlers.setError(null);
  } catch (error) {
    handlers.setAccount(null);
    if (error instanceof AuthSessionRequiredError && handlers.navigate) {
      handlers.setError(null);
      handlers.navigate("/sign-in?return_to=%2Faccount");
      return;
    }
    handlers.setError(handlers.genericError);
  }
}

export function SecurityConfirmationPanel({
  account,
  reload,
}: {
  account: AccountSnapshot;
  reload: () => Promise<void>;
}) {
  const { api, locale, t } = useAuthUi();
  const [password, setPassword] = useState("");
  const action = useAction();
  if (!account.reauthentication.required) return null;

  const methods = new Set(account.reauthentication.methods);
  async function confirm(
    operation: () => Promise<ActionResult>,
    reloadAfterSuccess = true,
  ) {
    const result = await action.run(operation);
    if (result.ok && reloadAfterSuccess && !result.redirectTo) {
      setPassword("");
      await reload();
    }
  }

  return <AccountSection
    title={t("reauthenticationTitle")}
    intro={t("reauthenticationIntro")}
  >
    <div className="notice notice--warning" role="status">{t("reauthenticationRequired")}</div>
    {methods.has("password") ? <Form onSubmit={() => confirm(() => api.reauthenticatePassword({
      email: account.user.email,
      password,
    }))}>
      <TextField
        label={t("confirmationPassword")}
        type="password"
        autoComplete="current-password"
        required
        value={password}
        onChange={(event) => setPassword(event.target.value)}
      />
      <SubmitButton busy={action.busy}>{t("confirmWithPassword")}</SubmitButton>
    </Form> : null}
    <div className="button-row reauthentication-actions">
      {methods.has("passkey") ? <ActionButton
        busy={action.busy}
        onClick={() => void confirm(() => api.reauthenticatePasskey())}
      >{t("confirmWithPasskey")}</ActionButton> : null}
      {methods.has("google") ? <ActionButton
        busy={action.busy}
        onClick={() => void confirm(() => api.reauthenticateGoogle(), false)}
      >{t("confirmWithGoogle")}</ActionButton> : null}
      {methods.has("magic-link") ? <ActionButton
        busy={action.busy}
        onClick={() => void confirm(() => api.requestReauthenticationMagicLink({
          email: account.user.email,
          locale,
        }), false)}
      >{t("confirmWithMagicLink")}</ActionButton> : null}
    </div>
    <ResultNotice result={action.result} />
  </AccountSection>;
}

function ProfilePanel({
  account,
  mutate,
  securityLocked,
}: {
  account: AccountSnapshot;
  mutate: AccountMutation;
  securityLocked: boolean;
}) {
  const { api, locale, t } = useAuthUi();
  const [name, setName] = useState(account.user.name);
  const [email, setEmail] = useState(account.user.email);
  const [busy, setBusy] = useState<"profile" | "email" | null>(null);
  const submitProfile = async () => {
    setBusy("profile");
    await mutate(() => api.updateProfile({ name }));
    setBusy(null);
  };
  const submitEmail = async () => {
    if (securityLocked) return;
    setBusy("email");
    await mutate(() => api.requestEmailChange({ email, locale }));
    setBusy(null);
  };
  return <AccountSection title={t("profile")}>
    <div className="account-columns">
      <Form onSubmit={submitProfile}>
        <TextField label={t("name")} autoComplete="name" required value={name} onChange={(event) => setName(event.target.value)} />
        <SubmitButton busy={busy === "profile"}>{t("save")}</SubmitButton>
      </Form>
      <Form onSubmit={submitEmail}>
        <TextField label={t("emailChange")} type="email" autoComplete="email" required value={email} onChange={(event) => setEmail(event.target.value)} hint={t("emailChangeHint")} />
        <SubmitButton busy={busy === "email"} disabled={securityLocked}>{t("save")}</SubmitButton>
      </Form>
    </div>
  </AccountSection>;
}

function PasswordPanel({
  hasPassword,
  mutate,
  securityLocked,
}: {
  hasPassword: boolean;
  mutate: AccountMutation;
  securityLocked: boolean;
}) {
  const { api, t } = useAuthUi();
  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const action = useAction();
  async function submit() {
    if (!hasPassword && securityLocked) return;
    if (password !== confirmation) return action.setResult({ ok: false, message: t("passwordsDiffer") });
    if (password.length < 8 || password.length > 128) {
      return action.setResult({ ok: false, message: t("passwordLengthError") });
    }
    const input = hasPassword ? { currentPassword, password } : { password };
    const result = await action.run(async () => {
      const next = await api.setPassword(input);
      if (next.code === "PASSWORD_TOO_SHORT" || next.code === "PASSWORD_TOO_LONG") {
        return { ...next, message: t("passwordLengthError") };
      }
      if (next.code === "INVALID_PASSWORD") {
        return { ...next, message: t("currentPasswordIncorrect") };
      }
      if (next.code === "PASSWORD_COMPROMISED") {
        return { ...next, message: t("passwordCompromised") };
      }
      return next;
    });
    if (result.ok) {
      setCurrentPassword("");
      setPassword("");
      setConfirmation("");
      await mutate(() => result);
    }
  }
  return <AccountSection title={t("passwordSet")}>
    <Form onSubmit={submit}>
      {hasPassword ? <TextField label={t("currentPassword")} type="password" autoComplete="current-password" required value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} /> : null}
      <TextField label={t("newPassword")} type="password" autoComplete="new-password" minLength={8} maxLength={128} required value={password} onChange={(event) => setPassword(event.target.value)} hint={t("passwordHint")} />
      <TextField label={t("confirmPassword")} type="password" autoComplete="new-password" required value={confirmation} onChange={(event) => setConfirmation(event.target.value)} />
      <SubmitButton busy={action.busy} disabled={!hasPassword && securityLocked}>{t("save")}</SubmitButton>
    </Form>
    <ResultNotice result={action.result} />
  </AccountSection>;
}

function IdentitiesPanel({
  account,
  mutate,
  securityLocked,
}: {
  account: AccountSnapshot;
  mutate: AccountMutation;
  securityLocked: boolean;
}) {
  const { api, locale, t } = useAuthUi();
  const [busyId, setBusyId] = useState<string | null>(null);
  const google = account.identities.find((identity) => identity.provider === "google");
  async function invoke(id: string, action: () => Promise<ActionResult>) {
    setBusyId(id);
    await mutate(action);
    setBusyId(null);
  }
  return <AccountSection title={t("identities")}>
    <ul className="item-list">
      {account.identities.map((identity) => <li key={identity.id}>
        <div><strong>{identity.label}</strong><small>{t("linked", { date: formatDate(locale, identity.linkedAt) })}</small></div>
        {identity.provider === "google" && identity.removable ? <ActionButton
          busy={busyId === identity.id}
          disabled={securityLocked}
          onClick={() => void invoke(identity.id, () => api.unlinkIdentity({ identityId: identity.id }))}
        >{t("unlink")}</ActionButton> : null}
      </li>)}
    </ul>
    {!google ? <ActionButton
      busy={busyId === "google"}
      disabled={securityLocked}
      onClick={() => void invoke("google", () => api.linkIdentity({ provider: "google" }))}
    >{t("linkGoogle")}</ActionButton> : null}
  </AccountSection>;
}

function PasskeyItem({
  passkey,
  mutate,
  securityLocked,
}: {
  passkey: AccountPasskey;
  mutate: AccountMutation;
  securityLocked: boolean;
}) {
  const { api, locale, t } = useAuthUi();
  const [name, setName] = useState(passkey.name);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  async function invoke(action: () => Promise<ActionResult>) {
    setBusy(true);
    await mutate(action);
    setBusy(false);
  }
  return <li>
    <div className="item-main">
      {editing ? <Form className="inline-form" onSubmit={async () => { await invoke(() => api.renamePasskey({ passkeyId: passkey.id, name })); setEditing(false); }}><TextField label={t("passkeyName")} value={name} required onChange={(event) => setName(event.target.value)} /><SubmitButton busy={busy}>{t("save")}</SubmitButton></Form> : <><strong>{passkey.name}</strong><small>{passkey.lastUsedAt ? t("lastUsed", { date: formatDate(locale, passkey.lastUsedAt) }) : t("createdOn", { date: formatDate(locale, passkey.createdAt) })}</small></>}
    </div>
    <div className="compact-actions">
      {!editing ? <ActionButton busy={busy} onClick={() => setEditing(true)}>{t("rename")}</ActionButton> : null}
      <ActionButton busy={busy} disabled={securityLocked} tone="danger" onClick={() => void invoke(() => api.removePasskey({ passkeyId: passkey.id }))}>{t("remove")}</ActionButton>
    </div>
  </li>;
}

function PasskeysPanel({
  account,
  mutate,
  securityLocked,
}: {
  account: AccountSnapshot;
  mutate: AccountMutation;
  securityLocked: boolean;
}) {
  const { api, t } = useAuthUi();
  const [name, setName] = useState("");
  const action = useAction();
  async function add() {
    if (securityLocked) return;
    const result = await action.run(() => api.addPasskey({ name }));
    if (result.ok) {
      setName("");
      await mutate(() => result);
    }
  }
  return <AccountSection title={t("passkeys")} intro={t("passkeyHint")}>
    <ul className="item-list">{account.passkeys.map((passkey) => <PasskeyItem key={passkey.id} passkey={passkey} mutate={mutate} securityLocked={securityLocked} />)}</ul>
    <Form className="inline-form add-form" onSubmit={add}>
      <TextField label={t("passkeyName")} required maxLength={64} value={name} onChange={(event) => setName(event.target.value)} />
      <SubmitButton busy={action.busy} disabled={securityLocked}>{t("addPasskey")}</SubmitButton>
    </Form>
    <ResultNotice result={action.result} />
  </AccountSection>;
}

function SessionsPanel({ account, mutate }: { account: AccountSnapshot; mutate: AccountMutation }) {
  const { api, locale, t } = useAuthUi();
  const [busyId, setBusyId] = useState<string | null>(null);
  async function invoke(id: string, action: () => Promise<ActionResult>) {
    setBusyId(id);
    await mutate(action);
    setBusyId(null);
  }
  return <AccountSection title={t("sessions")} intro={t("sessionsHint")}>
    <ul className="item-list">
      {account.sessions.map((session) => <li key={session.id}>
        <div><strong>{session.device || t("unknownDevice")} {session.current ? <span className="badge">{t("currentSession")}</span> : null}</strong><small>{[session.browser, session.ipHint, t("sessionCreated", { date: formatDate(locale, session.createdAt) })].filter(Boolean).join(" · ")}</small></div>
        {!session.current ? <ActionButton busy={busyId === session.id} onClick={() => void invoke(session.id, () => api.revokeSession({ sessionId: session.id }))}>{t("revoke")}</ActionButton> : null}
      </li>)}
    </ul>
    <div className="button-row"><ActionButton busy={busyId === "others"} onClick={() => void invoke("others", () => api.revokeOtherSessions())}>{t("revokeOthers")}</ActionButton><ActionButton busy={busyId === "logout"} onClick={() => void invoke("logout", () => api.logout({ allDevices: false }))}>{t("logout")}</ActionButton><ActionButton busy={busyId === "all"} tone="danger" onClick={() => void invoke("all", () => api.logout({ allDevices: true }))}>{t("logoutEverywhere")}</ActionButton></div>
  </AccountSection>;
}

function DeletionPanel({
  account,
  mutate,
  securityLocked,
}: {
  account: AccountSnapshot;
  mutate: AccountMutation;
  securityLocked: boolean;
}) {
  const { api, locale, t } = useAuthUi();
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const pending = account.user.deletionRequestedAt && account.user.deletionEffectiveAt;
  async function invoke(action: () => Promise<ActionResult>) {
    setBusy(true);
    await mutate(action);
    setBusy(false);
  }
  return <AccountSection title={t("dangerZone")} intro={pending ? t("deletionPending", { date: formatDate(locale, account.user.deletionEffectiveAt ?? "") }) : t("deletionIntro")}>
    {pending ? <ActionButton busy={busy} onClick={() => void invoke(() => api.cancelAccountDeletion())}>{t("cancelDeletion")}</ActionButton> : <>
      <label className="check confirm-check"><input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} /> {t("confirmDestructive")}</label>
      <button className="button button--danger" type="button" disabled={!confirmed || busy || securityLocked} onClick={() => void invoke(() => api.requestAccountDeletion())}>{busy ? t("loading") : t("requestDeletion")}</button>
    </>}
  </AccountSection>;
}

export function AccountPage({ searchParams }: AuthPageProps) {
  const { api, locale, onNavigate, t } = useAuthUi();
  const [account, setAccount] = useState<AccountSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<ActionResult | null>(() => {
    const linkError = searchParams?.get("error");
    if (!linkError) {
      return searchParams?.get("reauthenticated")
        ? { ok: true, message: t("reauthenticationConfirmed") }
        : null;
    }
    const mismatch = linkError === "email_doesn't_match"
      || linkError === "LINKING_DIFFERENT_EMAILS_NOT_ALLOWED";
    return {
      ok: false,
      message: t(mismatch ? "identityEmailMismatch" : "identityLinkFailed"),
    };
  });
  const load = useCallback(
    () => loadAccountSnapshot(api, {
      genericError: t("genericError"),
      navigate: onNavigate,
      setAccount,
      setError,
    }),
    [api, onNavigate, t],
  );
  useEffect(() => { void load(); }, [load]);
  const mutate: AccountMutation = (action) => runAccountMutation(async () => {
    const result = await action();
    return result.code === "SESSION_NOT_FRESH"
      ? { ...result, message: t("reauthenticationRequired") }
      : result;
  }, {
    genericError: t("genericError"),
    successMessage: t("actionCompleted"),
    navigate: onNavigate,
    reload: load,
    setNotice,
  });
  return <AuthShell title={t("account")} wide>
    {error ? <div className="notice notice--error" role="alert">{error}</div> : null}
    <ResultNotice result={notice} />
    {!account && !error ? <p role="status">{t("loading")}</p> : null}
    {account ? <div className="account-stack">
      {account.user.deletionRequestedAt ? <div className="notice notice--warning">{t("deletionPending", { date: formatDate(locale, account.user.deletionEffectiveAt ?? "") })}</div> : null}
      <SecurityConfirmationPanel account={account} reload={load} />
      <ProfilePanel account={account} mutate={mutate} securityLocked={account.reauthentication.required} />
      <PasswordPanel hasPassword={account.hasPassword} mutate={mutate} securityLocked={account.reauthentication.required} />
      <IdentitiesPanel account={account} mutate={mutate} securityLocked={account.reauthentication.required} />
      <PasskeysPanel account={account} mutate={mutate} securityLocked={account.reauthentication.required} />
      <SessionsPanel account={account} mutate={mutate} />
      <DeletionPanel account={account} mutate={mutate} securityLocked={account.reauthentication.required} />
    </div> : null}
  </AuthShell>;
}
